<template>
  <!-- prettier-ignore -->
  <div
    v-if="firstPerson"
    class="PackagePreview"
    :class="{ once: $props.once, pointable: available }"
    @click="onCardClicked"
  >
    <div
      v-if="showIllustration"
      class="package-illustration"
      :style="`background-image: url(${item.image})`"
      :alt="item.name"
    />
    <div class="content">
      <the-icon
        class="caret"
        art="light"
        name="chevron-right"
      />
      <h2
        :class="{ 'not-available': !available }"
        v-text="item.name"
      />
      <div
        v-if="available"
        class="available">
        <p
          class="intro"
          v-text="item.intro"
        />
        <ul class="bullets">
          <li
            v-for="(bullet, index) in item.bulletPoints"
            :key="index"
            v-text="bullet"
          />
        </ul>
        <div class="price">
          <span>{{ `${$t('product.currency')} ${price}` }}</span>
          <the-icon
            v-if="showInfoIcon"
            v-tooltip="offerText"
            art="light"
            name="circle-info"
          />
          <p
            v-else
            class="offer"
            v-text="offerText"
          />
        </div>
      </div>
      <div
        v-else
        class="not-available"
      >
        <h3 v-text="getLabel('preview_notavailable_title')" />
        <p v-text="getLabel('preview_notavailable_text')" />
      </div>

      <Form.Button
        class="moreinfo-button"
        secondary
        :text="getLabel('preview_button_moreinfo')"
      />
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { currency } from '@/modules/PartnerWidget/helpers/formatters'

import * as Form from '@/components/Form'

import usePartnerWidget from '@/hooks/usePartnerWidget'

const { getLabel, getPackageTotalPrice, isPackageAvailable, getOfferText, partnerWidgetDisplayMode } =
  usePartnerWidget()

// Props
const props = defineProps({
  item: {
    type: Object,
    default: () => ({}),
  },
  once: {
    type: Boolean,
    default: false,
  },
  showInfoIcon: {
    type: Boolean,
    default: false,
  },
})

// Init
const emit = defineEmits(['clicked'])

// Computed
const showIllustration = computed(() => {
  return partnerWidgetDisplayMode.value === 'wide'
})

const offerText = computed(() => {
  return getOfferText(props.item.basket.persons, props.item.showExampleOfferText)
})

const available = computed(() => {
  return isPackageAvailable(props.item)
})

const firstPerson = computed(() => {
  return props.item.basket?.persons[0]
})

const price = computed(() => {
  if (!available.value) return ''
  return currency(getPackageTotalPrice(props.item.products, firstPerson.value))
})

// Methods
function onCardClicked() {
  emit('clicked')
}
</script>

<style scoped>
.PackagePreview {
  background-color: var(--c-primary-neutral-3);
  border: 1px solid var(--c-secondary-neutral-3);
  position: relative;

  &.pointable {
    cursor: pointer;
  }

  .caret {
    position: absolute;
    right: 16px;
    top: 18px;
    font-size: 18px;

    &--unavailable {
      color: var(--c-secondary-neutral-3);
    }
  }

  &.once {
    .package-illustration.package-illustration {
      height: 250px;
      background-position: initial;
    }
  }

  h2 {
    font-size: var(--pw-font-size-large);
    margin: 0 0 10px 0;
    color: var(--c-primary-neutral-1);

    &.not-available {
      color: var(--c-secondary-neutral-2);
    }
  }

  p {
    margin: 0 0 10px;
    font-size: var(--pw-font-size-medium);
    line-height: var(--pw-line-height-medium);
  }

  .price {
    font-size: var(--pw-font-size-large);
    line-height: var(--pw-line-height-large);

    svg {
      padding-left: 5px;
    }

    p {
      margin: 0;
    }
  }

  div.not-available {
    background-color: var(--c-primary-neutral-2);
    flex: 1 0 auto;
    padding: 15px 20px;
    margin-bottom: 10px;

    h3 {
      font-size: var(--pw-font-size-medium);
      font-weight: 700;
      margin: 0 0 5px;
    }
  }

  /* type dependant stylings */

  .narrow & {
    padding: 15px;

    .content {
      .moreinfo-button {
        display: none;
      }

      .bullets {
        display: none;
      }

      .offer {
        font-size: var(--pw-font-size-offer);
      }
    }
  }

  .wide &,
  .short & {
    .package-illustration {
      max-width: 100%;
      height: 200px;
      display: block;
      background-position: center;
      background-repeat: no-repeat;
      background-size: cover;
      -ms-interpolation-mode: bicubic;
      -webkit-background-size: cover;
      -moz-background-size: cover;
      -o-background-size: cover;
    }

    .content {
      padding: 15px;
      flex: 1 0 auto;
      display: flex;
      flex-direction: column;

      .caret {
        display: none;
      }

      .available {
        display: flex;
        flex-direction: column;
        flex: 1 0 auto;

        .bullets {
          flex: 1 0 auto;
          padding-left: 20px;
          margin: 10px 0;

          li {
            list-style-type: square;
          }
        }
      }

      .offer {
        font-size: var(--pw-font-size-offer);
        margin-bottom: 10px;
      }
    }
  }
}
</style>
<style name="short" scoped>
.PackagePreview {
  .short & {
    .intro {
      display: none;
    }
  }
}
</style>
